import { useState } from "react";
import { useSiteSettings } from "../../../contexts/SiteSettingsContext";
import type { NavItem } from "../../../types/settings";

let nextId = Date.now();
const uid = () => String(++nextId);

export default function NavTab() {
  const { settings, updateSettings } = useSiteSettings();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const updateItem = (id: string, patch: Partial<NavItem>) => {
    updateSettings({
      navItems: settings.navItems.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    });
  };

  const removeItem = (id: string) => {
    updateSettings({ navItems: settings.navItems.filter((n) => n.id !== id) });
  };

  const addItem = () => {
    const newItem: NavItem = { id: uid(), label: "新しいリンク", href: "https://" };
    updateSettings({ navItems: [...settings.navItems, newItem] });
  };

  const moveItem = (id: string, dir: "up" | "down") => {
    const arr = [...settings.navItems];
    const idx = arr.findIndex((n) => n.id === id);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    updateSettings({ navItems: arr });
  };

  const addChildItem = (parentId: string) => {
    updateSettings({
      navItems: settings.navItems.map((n) => {
        if (n.id !== parentId) return n;
        return {
          ...n,
          children: [...(n.children || []), { label: "新しいサブリンク", href: "https://" }],
        };
      }),
    });
  };

  const updateChildItem = (parentId: string, childIdx: number, field: "label" | "href", value: string) => {
    updateSettings({
      navItems: settings.navItems.map((n) => {
        if (n.id !== parentId || !n.children) return n;
        const children = n.children.map((c, i) => (i === childIdx ? { ...c, [field]: value } : c));
        return { ...n, children };
      }),
    });
  };

  const removeChildItem = (parentId: string, childIdx: number) => {
    updateSettings({
      navItems: settings.navItems.map((n) => {
        if (n.id !== parentId || !n.children) return n;
        return { ...n, children: n.children.filter((_, i) => i !== childIdx) };
      }),
    });
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-700 mb-2 pb-2 border-b border-gray-200">ナビゲーション設定</h3>
      <p className="text-xs text-gray-400 mb-4">メニュー項目の追加・削除・編集ができます。</p>

      <div className="space-y-2">
        {settings.navItems.map((item, idx) => (
          <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* メイン行 */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white">
              <div className="flex flex-col gap-0">
                <button onClick={() => moveItem(item.id, "up")} disabled={idx === 0}
                  className="w-5 h-4 flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed">
                  <i className="ri-arrow-up-s-line text-xs" />
                </button>
                <button onClick={() => moveItem(item.id, "down")} disabled={idx === settings.navItems.length - 1}
                  className="w-5 h-4 flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed">
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
              </div>
              <input
                type="text"
                value={item.label}
                onChange={(e) => updateItem(item.id, { label: e.target.value })}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-red-400 w-32"
                placeholder="ラベル"
              />
              <input
                type="url"
                value={item.href}
                onChange={(e) => updateItem(item.id, { href: e.target.value })}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-red-400 flex-1 min-w-0"
                placeholder="URL"
              />
              <button
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer"
                title="サブメニュー"
              >
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${expandedId === item.id ? "rotate-180" : ""}`} />
              </button>
              <button
                onClick={() => removeItem(item.id)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer"
                title="削除"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>

            {/* サブメニュー */}
            {expandedId === item.id && (
              <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 space-y-2">
                <p className="text-xs text-gray-500 mb-2">サブメニュー項目</p>
                {(item.children || []).map((child, ci) => (
                  <div key={ci} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={child.label}
                      onChange={(e) => updateChildItem(item.id, ci, "label", e.target.value)}
                      className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-red-400 w-32"
                      placeholder="ラベル"
                    />
                    <input
                      type="url"
                      value={child.href}
                      onChange={(e) => updateChildItem(item.id, ci, "href", e.target.value)}
                      className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-red-400 flex-1 min-w-0"
                      placeholder="URL"
                    />
                    <button
                      onClick={() => removeChildItem(item.id, ci)}
                      className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addChildItem(item.id)}
                  className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 cursor-pointer mt-1"
                >
                  <i className="ri-add-line" /> サブメニューを追加
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={addItem}
        className="mt-4 flex items-center gap-2 px-4 py-2 border border-dashed border-red-400 rounded-lg text-sm text-red-600 hover:bg-red-50 cursor-pointer w-full justify-center"
      >
        <i className="ri-add-line" /> メニュー項目を追加
      </button>
    </div>
  );
}
